import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { CreditCard, Save, Eye, EyeOff, CheckCircle, DollarSign, Layers, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import useCommPreflight from '../hooks/useCommPreflight';
import CommPreflightWarning from './CommPreflightWarning';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Default client-facing notice for down payments. Kept identical to the
// fallback in usePaymentSettings.js so admins see what clients will see if
// they leave the custom notice blank.
const DEFAULT_DOWN_PAYMENT_NOTICE =
  'Down payments are non-refundable. They are paid ahead of time and do not come back to you.';

const PaymentSettingsEditor = () => {
  const preflight = useCommPreflight();
  const [providers, setProviders] = useState([]);
  const [activeProviders, setActiveProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentWorkflow, setPaymentWorkflow] = useState('pay_first');
  const [savingWorkflow, setSavingWorkflow] = useState(false);

  // Global down-payment + installments settings. Stored per-provider in the
  // DB (one row per provider) but presented to admins as a single global
  // configuration. On save we sync the values to every configured provider
  // so clients see consistent behaviour regardless of which provider is
  // active. Loaded from the first active provider (or first configured) on
  // mount and after every save.
  const [globalSettings, setGlobalSettings] = useState({
    allow_partial_payment: false,
    partial_payment_percentage: 50,
    down_payment_type: 'percent',
    down_payment_fixed_amount: 0,
    down_payment_admin_can_override: false,
    down_payment_notice: '',
    allow_installments: false,
    installments_max_count: 4,
  });
  const [savingGlobal, setSavingGlobal] = useState(false);

  const [formData, setFormData] = useState({
    provider: '',
    is_enabled: false,
    // Stripe
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    // PayPal
    paypal_client_id: '',
    paypal_secret: '',
    paypal_mode: 'sandbox',
    // Square
    square_application_id: '',
    square_access_token: '',
    square_location_id: '',
    square_environment: 'sandbox',
    // General
    currency: 'USD',
    require_payment_upfront: false,
    allow_partial_payment: false,
    partial_payment_percentage: 50,
    // Role permissions
    allow_client_payments: true,
    allow_employee_payments: true,
    allow_admin_payments: true,
    allow_employee_partial_payments: true,
  });

  useEffect(() => {
    loadProviders();
    loadPaymentWorkflow();
  }, []);

  const loadPaymentWorkflow = async () => {
    try {
      const response = await axios.get(`${API_URL}/business-info/payment_workflow`);
      setPaymentWorkflow(response.data.info_value || 'pay_first');
    } catch (error) {
      console.log('Payment workflow not set, using default');
      setPaymentWorkflow('pay_first');
    }
  };

  const handleWorkflowChange = async (workflow) => {
    setSavingWorkflow(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/business-info/payment_workflow`,
        { value: workflow },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentWorkflow(workflow);
      toast.success('Payment workflow updated successfully!');
    } catch (error) {
      console.error('Update workflow error:', error);
      toast.error('Failed to update payment workflow');
    } finally {
      setSavingWorkflow(false);
    }
  };

  const loadProviders = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/payment-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProviders(response.data);
      const actives = response.data.filter(p => p.is_active).map(p => p.provider);
      setActiveProviders(actives);

      // Hydrate global down-payment + installments settings from the first
      // active provider (preferred) or the first configured provider.
      if (response.data.length > 0) {
        const source = response.data.find(p => p.is_active) || response.data[0];
        setGlobalSettings({
          allow_partial_payment: source.allow_partial_payment || false,
          partial_payment_percentage: source.partial_payment_percentage || 50,
          down_payment_type: source.down_payment_type || 'percent',
          down_payment_fixed_amount: parseFloat(source.down_payment_fixed_amount) || 0,
          down_payment_admin_can_override: source.down_payment_admin_can_override || false,
          down_payment_notice: source.down_payment_notice || '',
          allow_installments: source.allow_installments || false,
          installments_max_count: parseInt(source.installments_max_count, 10) || 4,
        });
      }
    } catch (error) {
      console.error('Load payment providers error:', error);
      console.warn('Payment settings not configured yet');
    } finally {
      setLoading(false);
    }
  };

  // Sync the global down-payment + installments settings to every configured
  // provider so behaviour is consistent regardless of which provider is
  // active. POSTs to the existing /api/payment-settings endpoint (per
  // provider) without touching provider-specific credential fields, since
  // the backend uses COALESCE on omitted fields.
  const handleSaveGlobalSettings = async () => {
    if (providers.length === 0) {
      toast.warn('Configure at least one payment provider before setting down-payment defaults.');
      return;
    }
    setSavingGlobal(true);
    try {
      const token = sessionStorage.getItem('token');
      const payload = {
        allow_partial_payment: globalSettings.allow_partial_payment,
        partial_payment_percentage: globalSettings.partial_payment_percentage,
        down_payment_type: globalSettings.down_payment_type,
        down_payment_fixed_amount: globalSettings.down_payment_fixed_amount,
        down_payment_admin_can_override: globalSettings.down_payment_admin_can_override,
        down_payment_notice: globalSettings.down_payment_notice || null,
        allow_installments: globalSettings.allow_installments,
        installments_max_count: globalSettings.installments_max_count,
      };
      await Promise.all(providers.map(p =>
        axios.post(`${API_URL}/payment-settings`, { ...payload, provider: p.provider }, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ));
      toast.success('Down-payment and installment settings saved across all providers.');
      loadProviders();
    } catch (error) {
      console.error('Save global payment settings error:', error);
      toast.error('Failed to save settings');
    } finally {
      setSavingGlobal(false);
    }
  };

  const handleGlobalChange = (field, value) => {
    setGlobalSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    
    // Check if this provider already has saved settings
    const existingProvider = providers.find(p => p.provider === provider);
    
    if (existingProvider) {
      // Load existing settings
      setFormData({
        provider: existingProvider.provider,
        is_enabled: true, // Always enabled when configuring
        stripe_publishable_key: existingProvider.stripe_publishable_key || '',
        stripe_secret_key: existingProvider.stripe_secret_key || '',
        stripe_webhook_secret: existingProvider.stripe_webhook_secret || '',
        paypal_client_id: existingProvider.paypal_client_id || '',
        paypal_secret: existingProvider.paypal_secret || '',
        paypal_mode: existingProvider.paypal_mode || 'sandbox',
        square_application_id: existingProvider.square_application_id || '',
        square_access_token: existingProvider.square_access_token || '',
        square_location_id: existingProvider.square_location_id || '',
        square_environment: existingProvider.square_environment || 'sandbox',
        currency: existingProvider.currency || 'USD',
        require_payment_upfront: existingProvider.require_payment_upfront || false,
        allow_partial_payment: existingProvider.allow_partial_payment || false,
        partial_payment_percentage: existingProvider.partial_payment_percentage || 50,
        allow_client_payments: existingProvider.allow_client_payments !== false, // Default to true if not set
        allow_employee_payments: existingProvider.allow_employee_payments !== false,
        allow_admin_payments: existingProvider.allow_admin_payments !== false,
        allow_employee_partial_payments: existingProvider.allow_employee_partial_payments !== false,
      });
    } else {
      // New provider - use defaults
      setFormData({
        provider: provider,
        is_enabled: true, // Auto-enable when configuring
        stripe_publishable_key: '',
        stripe_secret_key: '',
        stripe_webhook_secret: '',
        paypal_client_id: '',
        paypal_secret: '',
        paypal_mode: 'sandbox',
        square_application_id: '',
        square_access_token: '',
        square_location_id: '',
        square_environment: 'sandbox',
        currency: 'USD',
        require_payment_upfront: false,
        allow_partial_payment: false,
        partial_payment_percentage: 50,
        allow_client_payments: true,
        allow_employee_payments: true,
        allow_admin_payments: true,
        allow_employee_partial_payments: true,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      
      // Convert empty strings to null so COALESCE works properly
      const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
        acc[key] = (typeof value === 'string' && value.trim() === '') ? null : value;
        return acc;
      }, {});
      
      await axios.post(`${API_URL}/payment-settings`, cleanedData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Payment settings saved successfully!');
      setSelectedProvider(null);
      loadProviders();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save payment settings');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (provider) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(`${API_URL}/payment-settings/activate/${provider}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${provider.toUpperCase()} activated successfully!`);
      loadProviders();
    } catch (error) {
      console.error('Activate error:', error);
      toast.error('Failed to activate provider');
    }
  };

  const handleDeactivate = async (provider) => {
    try {
      const token = sessionStorage.getItem('token');
      await axios.patch(`${API_URL}/payment-settings/deactivate/${provider}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${provider.toUpperCase()} deactivated successfully!`);
      loadProviders();
    } catch (error) {
      console.error('Deactivate error:', error);
      toast.error('Failed to deactivate provider');
    }
  };

  const renderProviderForm = () => {
    if (!selectedProvider) return null;

    return (
      <div className="card mb-6 bg-blue-50 border-2 border-blue-200">
        <h3 className="text-xl font-bold mb-4">Configure {selectedProvider.toUpperCase()}</h3>

        {/* Stripe Form */}
        {selectedProvider === 'stripe' && (
          <div className="space-y-4">
            <div>
              <label className="label">Publishable Key</label>
              <input
                type="text"
                value={formData.stripe_publishable_key}
                onChange={(e) => handleInputChange('stripe_publishable_key', e.target.value)}
                className="input"
                placeholder="pk_test_..."
              />
            </div>
            <div>
              <label className="label">Secret Key</label>
              <div className="relative">
                <input
                  type={showSecrets.stripe_secret ? 'text' : 'password'}
                  value={formData.stripe_secret_key}
                  onChange={(e) => handleInputChange('stripe_secret_key', e.target.value)}
                  className="input pr-10"
                  placeholder="sk_test_..."
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('stripe_secret')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showSecrets.stripe_secret ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">Webhook Secret (Optional)</label>
              <input
                type={showSecrets.stripe_webhook ? 'text' : 'password'}
                value={formData.stripe_webhook_secret}
                onChange={(e) => handleInputChange('stripe_webhook_secret', e.target.value)}
                className="input"
                placeholder="whsec_..."
              />
            </div>
          </div>
        )}

        {/* PayPal Form */}
        {selectedProvider === 'paypal' && (
          <div className="space-y-4">
            <div>
              <label className="label">Client ID</label>
              <input
                type="text"
                value={formData.paypal_client_id}
                onChange={(e) => handleInputChange('paypal_client_id', e.target.value)}
                className="input"
                placeholder="AYSq3RDGsmBLJE-otTkBtM-jBc..."
              />
            </div>
            <div>
              <label className="label">Secret</label>
              <div className="relative">
                <input
                  type={showSecrets.paypal_secret ? 'text' : 'password'}
                  value={formData.paypal_secret}
                  onChange={(e) => handleInputChange('paypal_secret', e.target.value)}
                  className="input pr-10"
                  placeholder="EGnHDxD_qRPdaLdZz8iCr8N7..."
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('paypal_secret')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showSecrets.paypal_secret ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">Mode</label>
              <select
                value={formData.paypal_mode}
                onChange={(e) => handleInputChange('paypal_mode', e.target.value)}
                className="input"
              >
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="live">Live (Production)</option>
              </select>
            </div>
          </div>
        )}

        {/* Square Form */}
        {selectedProvider === 'square' && (
          <div className="space-y-4">
            <div>
              <label className="label">Application ID</label>
              <input
                type="text"
                value={formData.square_application_id}
                onChange={(e) => handleInputChange('square_application_id', e.target.value)}
                className="input"
                placeholder="sandbox-sq0idb-..."
              />
            </div>
            <div>
              <label className="label">Access Token</label>
              <div className="relative">
                <input
                  type={showSecrets.square_token ? 'text' : 'password'}
                  value={formData.square_access_token}
                  onChange={(e) => handleInputChange('square_access_token', e.target.value)}
                  className="input pr-10"
                  placeholder="EAAAEOuLQhGfPpe..."
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('square_token')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showSecrets.square_token ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">Location ID</label>
              <input
                type="text"
                value={formData.square_location_id}
                onChange={(e) => handleInputChange('square_location_id', e.target.value)}
                className="input"
                placeholder="LH2C8ZXE..."
              />
            </div>
            <div>
              <label className="label">Environment</label>
              <select
                value={formData.square_environment}
                onChange={(e) => handleInputChange('square_environment', e.target.value)}
                className="input"
              >
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production (Live)</option>
              </select>
            </div>
          </div>
        )}

        {/* General Settings */}
        <div className="mt-6 pt-6 border-t border-blue-300 space-y-4">
          <h4 className="font-semibold">General Settings</h4>
          
          <div>
            <label className="label">Currency</label>
            <select
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="input"
            >
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="CAD">CAD - Canadian Dollar</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="require_payment"
              checked={formData.require_payment_upfront}
              onChange={(e) => handleInputChange('require_payment_upfront', e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="require_payment" className="text-sm font-medium text-gray-700">
              Require payment at time of booking
            </label>
          </div>

          <div className="p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600">
            Down-payment and installment options are now configured globally
            (above this section) so they apply consistently across all
            providers. Provider-specific overrides are no longer set here.
          </div>
        </div>

        {/* Role-Based Payment Permissions */}
        {selectedProvider !== 'cash_only' && (
          <div className="mt-6 pt-6 border-t border-blue-300 space-y-4">
            <h4 className="font-semibold">Payment Permissions by Role</h4>
            <p className="text-sm text-gray-600">Control which user roles can process payments during booking</p>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="allow_client_payments"
                checked={formData.allow_client_payments}
                onChange={(e) => handleInputChange('allow_client_payments', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="allow_client_payments" className="text-sm font-medium text-gray-700">
                Allow clients to pay online when booking
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="allow_employee_payments"
                checked={formData.allow_employee_payments}
                onChange={(e) => handleInputChange('allow_employee_payments', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="allow_employee_payments" className="text-sm font-medium text-gray-700">
                Allow employees to process payments when booking for clients
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="allow_admin_payments"
                checked={formData.allow_admin_payments}
                onChange={(e) => handleInputChange('allow_admin_payments', e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <label htmlFor="allow_admin_payments" className="text-sm font-medium text-gray-700">
                Allow admins to process payments when booking for clients
              </label>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Partial Payment Permissions</h5>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="allow_employee_partial"
                  checked={formData.allow_employee_partial_payments}
                  onChange={(e) => handleInputChange('allow_employee_partial_payments', e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="allow_employee_partial" className="text-sm font-medium text-gray-700">
                  Allow employees to process partial payments
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Admins can always process partial payments. Clients cannot process partial payments.
              </p>
            </div>

            <div className="p-3 bg-blue-100 border border-blue-300 rounded text-sm text-blue-800">
              <strong>Tip:</strong> You can disable client payments but allow employees/admins to process payments in-person or over the phone.
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-6">
          <button onClick={() => setSelectedProvider(null)} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary flex items-center space-x-2">
            <Save size={18} />
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <CreditCard className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Payment Settings</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Configure payment providers to allow clients to pay online. You can activate multiple providers and clients will be able to choose their preferred payment method.
      </p>

      {/* Payment Workflow Section */}
      <div className="card bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-3">Payment Workflow</h3>
        <p className="text-sm text-gray-600 mb-4">
          Control when clients pay for their appointments during the booking process.
        </p>
        
        <div className="flex items-start space-x-4 p-4 bg-white border-2 border-gray-300 rounded-lg">
          <div className="flex items-center h-6">
            <input
              type="checkbox"
              id="approve_first_toggle"
              checked={paymentWorkflow === 'approve_first'}
              onChange={(e) => handleWorkflowChange(e.target.checked ? 'approve_first' : 'pay_first')}
              disabled={savingWorkflow}
              className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="approve_first_toggle" className="font-semibold text-gray-900 cursor-pointer">
              Require Admin Approval Before Payment
            </label>
            <div className="mt-2 text-sm text-gray-600 space-y-2">
              <div className="flex items-start space-x-2">
                <span className="font-medium text-purple-600">When OFF (default):</span>
                <span>Client pays immediately when booking → Admin reviews → If declined, automatic refund issued</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="font-medium text-green-600">When ON:</span>
                <span>Client books → Admin approves → Payment link sent to client → Client pays</span>
              </div>
            </div>
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <strong>💡 Tip:</strong> Enable this if you want to screen appointments before collecting payment. 
              Disable for immediate payment to reduce no-shows.
            </div>
          </div>
        </div>

        {savingWorkflow && (
          <div className="mt-3 text-sm text-purple-600 flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
            <span>Saving...</span>
          </div>
        )}
      </div>

      {/* Down Payments — top-level global settings card. Applies across all
          configured providers so behaviour is consistent for clients. */}
      <div className="card bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 mb-6">
        <div className="flex items-center space-x-3 mb-3">
          <DollarSign className="text-emerald-700" size={24} />
          <h3 className="text-lg font-bold text-gray-900">Down Payments</h3>
        </div>

        {/* Preflight: down payments rely on the confirmation email reaching
            the client with the down-payment receipt. Block enablement when
            email isn't configured. */}
        {!preflight.loading && !preflight.emailConfigured && !globalSettings.allow_partial_payment && (
          <CommPreflightWarning
            channel="email"
            reason={preflight.emailReason}
            featureName="down payments"
          />
        )}
        <p className="text-sm text-gray-700 mb-4">
          Let clients pay a portion of the total at booking time as a
          <strong> non-refundable down payment</strong>. The remaining balance
          is collected at the appointment. <em>Down payments are not deposits</em> —
          they are paid ahead of time and do not come back to the client.
        </p>

        <label className={`flex items-start space-x-3 p-3 bg-white border-2 border-gray-300 rounded-lg mb-4 ${
          !preflight.emailConfigured && !globalSettings.allow_partial_payment ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        }`}>
          <input
            type="checkbox"
            checked={globalSettings.allow_partial_payment}
            disabled={!preflight.emailConfigured && !globalSettings.allow_partial_payment}
            onChange={(e) => {
              if (e.target.checked && !preflight.emailConfigured) {
                toast.error('Configure email settings before enabling down payments.');
                return;
              }
              handleGlobalChange('allow_partial_payment', e.target.checked);
            }}
            className="mt-1 w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
          />
          <div className="flex-1">
            <span className="font-semibold text-gray-900">Allow down payments at booking</span>
            <p className="text-xs text-gray-600 mt-1">
              When on, clients see a "Pay down payment" option at checkout
              alongside "Pay full" and "Pay later".
            </p>
          </div>
        </label>

        {globalSettings.allow_partial_payment && (
          <div className="space-y-4 p-4 bg-white border border-emerald-200 rounded-lg">
            {/* Down-payment type */}
            <div>
              <label className="label">Default down-payment amount</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer ${
                  globalSettings.down_payment_type === 'percent'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-gray-200'
                }`}>
                  <input
                    type="radio"
                    name="dpType"
                    value="percent"
                    checked={globalSettings.down_payment_type === 'percent'}
                    onChange={() => handleGlobalChange('down_payment_type', 'percent')}
                    className="mt-1 w-4 h-4 text-emerald-600"
                  />
                  <div className="ml-2 flex-1">
                    <div className="font-semibold text-sm">Percent of total</div>
                    <div className="text-xs text-gray-600">
                      e.g. 50% of $200 → client pays $100 down
                    </div>
                  </div>
                </label>
                <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer ${
                  globalSettings.down_payment_type === 'fixed'
                    ? 'border-emerald-600 bg-emerald-50'
                    : 'border-gray-200'
                }`}>
                  <input
                    type="radio"
                    name="dpType"
                    value="fixed"
                    checked={globalSettings.down_payment_type === 'fixed'}
                    onChange={() => handleGlobalChange('down_payment_type', 'fixed')}
                    className="mt-1 w-4 h-4 text-emerald-600"
                  />
                  <div className="ml-2 flex-1">
                    <div className="font-semibold text-sm">Fixed dollar amount</div>
                    <div className="text-xs text-gray-600">
                      e.g. $50 down regardless of total
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* Value */}
            {globalSettings.down_payment_type === 'percent' ? (
              <div>
                <label className="label">Percentage</label>
                <div className="relative w-40">
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={globalSettings.partial_payment_percentage}
                    onChange={(e) => handleGlobalChange('partial_payment_percentage', parseFloat(e.target.value) || 0)}
                    className="input pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
              </div>
            ) : (
              <div>
                <label className="label">Fixed amount</label>
                <div className="relative w-40">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={globalSettings.down_payment_fixed_amount}
                    onChange={(e) => handleGlobalChange('down_payment_fixed_amount', parseFloat(e.target.value) || 0)}
                    className="input pl-8"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Will be capped at the appointment total if the total is lower.
                </p>
              </div>
            )}

            {/* Admin override */}
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={globalSettings.down_payment_admin_can_override}
                onChange={(e) => handleGlobalChange('down_payment_admin_can_override', e.target.checked)}
                className="mt-1 w-5 h-5 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <div className="flex-1">
                <span className="font-semibold text-sm text-gray-900">Let admin set the down-payment amount per appointment</span>
                <p className="text-xs text-gray-600 mt-1">
                  When on, admin can override the default amount on a
                  per-appointment basis before the payment link goes out.
                  When off, all clients pay the default value above.
                </p>
              </div>
            </label>

            {/* Non-refundable notice */}
            <div>
              <label className="label flex items-center space-x-2">
                <AlertTriangle size={16} className="text-amber-600" />
                <span>Non-refundable notice (shown to clients at checkout)</span>
              </label>
              <textarea
                value={globalSettings.down_payment_notice}
                onChange={(e) => handleGlobalChange('down_payment_notice', e.target.value)}
                className="input"
                rows="3"
                placeholder={DEFAULT_DOWN_PAYMENT_NOTICE}
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank to use the default text above. This notice appears
                next to the down-payment option during booking and on the
                payment confirmation screen.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Installments — top-level global settings card. Phase 2 surface;
          full backend (per-installment payment links + reminder schedule) is
          on the roadmap as a separate item. The toggle is admin-visible now
          so businesses can flag interest, but until backend ships the
          booking flow will show installments as "coming soon" rather than
          presenting non-functional payment options to clients. */}
      <div className="card bg-gradient-to-r from-sky-50 to-indigo-50 border-2 border-sky-200 mb-6">
        <div className="flex items-center space-x-3 mb-3">
          <Layers className="text-sky-700" size={24} />
          <h3 className="text-lg font-bold text-gray-900">Installments (Pay in Chunks)</h3>
        </div>
        <p className="text-sm text-gray-700 mb-4">
          Let clients split the full payment into multiple chunks at booking.
          Useful for larger services where some clients can't pay the full
          amount up front. <em>Different from a down payment</em>: with
          installments the client still pays the full total, just over time
          rather than all at once.
        </p>

        {/* Preflight: installments rely on per-chunk payment-link emails to
            the client. Block enablement when email isn't configured. */}
        {!preflight.loading && !preflight.emailConfigured && !globalSettings.allow_installments && (
          <CommPreflightWarning
            channel="email"
            reason={preflight.emailReason}
            featureName="installments"
          />
        )}

        <label className={`flex items-start space-x-3 p-3 bg-white border-2 border-gray-300 rounded-lg mb-4 ${
          !preflight.emailConfigured && !globalSettings.allow_installments ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
        }`}>
          <input
            type="checkbox"
            checked={globalSettings.allow_installments}
            disabled={!preflight.emailConfigured && !globalSettings.allow_installments}
            onChange={(e) => {
              if (e.target.checked && !preflight.emailConfigured) {
                toast.error('Configure email settings before enabling installments.');
                return;
              }
              handleGlobalChange('allow_installments', e.target.checked);
            }}
            className="mt-1 w-5 h-5 text-sky-600 rounded focus:ring-sky-500"
          />
          <div className="flex-1">
            <span className="font-semibold text-gray-900">Allow clients to pay in installments</span>
            <p className="text-xs text-gray-600 mt-1">
              When on, clients see a "Pay in 2/3/N installments" option at
              checkout. Each installment generates its own payment link.
            </p>
          </div>
        </label>

        {globalSettings.allow_installments && (
          <div className="space-y-4 p-4 bg-white border border-sky-200 rounded-lg">
            <div>
              <label className="label">Maximum number of installments</label>
              <div className="w-40">
                <input
                  type="number"
                  min="2"
                  max="12"
                  value={globalSettings.installments_max_count}
                  onChange={(e) => handleGlobalChange('installments_max_count', parseInt(e.target.value, 10) || 2)}
                  className="input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Clients will be able to pick any value from 2 up to this number.
              </p>
            </div>

            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-900">
              <strong>Phase 2a + 2b (shipped):</strong> Client booking flow is
              live — when enabled, clients see "Pay in Installments" at
              checkout, pick the chunk count, and set each chunk's amount and
              method (online or in-person). The first online chunk is paid at
              booking; in-person chunks are paid in cash at the appointment.
              <br /><br />
              On any partial-paid appointment row, the sky-blue
              <em> Manage Installments </em> button opens a modal listing
              every chunk with <em>Mark paid (cash / manual)</em> actions and,
              for online chunks #2+, a <em>Send link</em> button that emails
              the client a secure single-purpose payment URL (Stripe / PayPal,
              7-day expiry). The link page lets the client pay without
              logging in; the chunk auto-marks paid when the provider
              confirms.
              <br /><br />
              <strong>Still pending:</strong> the reminder cron that nudges
              clients before each chunk's due date. Until then, you can resend
              a link on demand from the Manage Installments modal.
            </div>
          </div>
        )}
      </div>

      {/* Save bar for the global settings (Down Payments + Installments) */}
      <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-6 flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">Save down-payment & installment settings</h4>
          <p className="text-sm text-gray-600">
            Applies to all configured providers. Provider credentials are not
            affected.
          </p>
        </div>
        <button
          onClick={handleSaveGlobalSettings}
          disabled={savingGlobal}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={18} />
          <span>{savingGlobal ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>

      {renderProviderForm()}

      {/* Provider Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Pay in Person */}
        <div className={`p-4 border-2 rounded-lg ${activeProviders.includes('cash_only') ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">Pay in Person</h3>
            {activeProviders.includes('cash_only') && <CheckCircle className="text-green-600" size={20} />}
          </div>
          <p className="text-sm text-gray-600 mb-4">No online payments. Clients pay when they arrive.</p>
          <div className="space-y-2">
            {!activeProviders.includes('cash_only') ? (
              <button
                onClick={() => handleActivate('cash_only')}
                className="btn btn-sm btn-secondary w-full"
              >
                Activate
              </button>
            ) : (
              <button
                onClick={() => handleDeactivate('cash_only')}
                className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200 w-full"
              >
                Deactivate
              </button>
            )}
          </div>
        </div>

        {/* Stripe */}
        <div className={`p-4 border-2 rounded-lg ${activeProviders.includes('stripe') ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">Stripe</h3>
            {activeProviders.includes('stripe') && <CheckCircle className="text-green-600" size={20} />}
          </div>
          <p className="text-sm text-gray-600 mb-4">Credit/debit cards, Apple Pay, Google Pay</p>
          <div className="space-y-2">
            <button
              onClick={() => handleProviderSelect('stripe')}
              className="btn btn-sm btn-primary w-full"
            >
              Configure
            </button>
            {providers.find(p => p.provider === 'stripe')?.is_configured && (
              !activeProviders.includes('stripe') ? (
                <button
                  onClick={() => handleActivate('stripe')}
                  className="btn btn-sm btn-secondary w-full"
                >
                  Activate
                </button>
              ) : (
                <button
                  onClick={() => handleDeactivate('stripe')}
                  className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200 w-full"
                >
                  Deactivate
                </button>
              )
            )}
          </div>
        </div>

        {/* PayPal */}
        <div className={`p-4 border-2 rounded-lg ${activeProviders.includes('paypal') ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">PayPal</h3>
            {activeProviders.includes('paypal') && <CheckCircle className="text-green-600" size={20} />}
          </div>
          <p className="text-sm text-gray-600 mb-4">PayPal balance, cards, bank accounts</p>
          <div className="space-y-2">
            <button
              onClick={() => handleProviderSelect('paypal')}
              className="btn btn-sm btn-primary w-full"
            >
              Configure
            </button>
            {providers.find(p => p.provider === 'paypal')?.is_configured && (
              !activeProviders.includes('paypal') ? (
                <button
                  onClick={() => handleActivate('paypal')}
                  className="btn btn-sm btn-secondary w-full"
                >
                  Activate
                </button>
              ) : (
                <button
                  onClick={() => handleDeactivate('paypal')}
                  className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200 w-full"
                >
                  Deactivate
                </button>
              )
            )}
          </div>
        </div>

        {/* Square */}
        <div className={`p-4 border-2 rounded-lg ${activeProviders.includes('square') ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">Square</h3>
            {activeProviders.includes('square') && <CheckCircle className="text-green-600" size={20} />}
          </div>
          <p className="text-sm text-gray-600 mb-4">Credit/debit cards, digital wallets</p>
          <div className="space-y-2">
            <button
              onClick={() => handleProviderSelect('square')}
              className="btn btn-sm btn-primary w-full"
            >
              Configure
            </button>
            {providers.find(p => p.provider === 'square')?.is_configured && (
              !activeProviders.includes('square') ? (
                <button
                  onClick={() => handleActivate('square')}
                  className="btn btn-sm btn-secondary w-full"
                >
                  Activate
                </button>
              ) : (
                <button
                  onClick={() => handleDeactivate('square')}
                  className="btn btn-sm bg-red-100 text-red-700 hover:bg-red-200 w-full"
                >
                  Deactivate
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> You'll need to create accounts with your chosen payment provider and obtain API keys. 
          Test in sandbox/test mode first before switching to production.
        </p>
      </div>
    </div>
  );
};

export default PaymentSettingsEditor;
